import { useEffect, useMemo } from 'react';
import { Navigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../components/SEO';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { PostHeader } from '../components/blog/PostHeader';
import { PostCard } from '../components/blog/PostCard';
import { AuthorBio } from '../components/blog/AuthorBio';
import { getPostBySlug, getRelatedPosts, getPostAlternates } from '../lib/blog';
import { useCurrentLocale, useLocalizedPath } from '../i18n/useLocalizedPath';

const SITE_URL = 'https://bolaoelite.com.br';

export default function BlogPost() {
  const { t } = useTranslation('blog');
  const locale = useCurrentLocale();
  const path = useLocalizedPath();
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug, locale) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const relatedPosts = useMemo(
    () => (slug ? getRelatedPosts(slug, locale, 3) : []),
    [slug, locale]
  );

  const blogPath = path('blog');
  const postUrl = post ? `${SITE_URL}${blogPath}/${post.slug}` : '';

  // hreflang alternates (pt-BR/en/es + x-default) — independem do locale ativo.
  const alternates = useMemo(
    () => (post ? getPostAlternates(post.slug) : []),
    [post]
  );

  const articleSchema = useMemo(() => {
    if (!post) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      image: `${SITE_URL}${post.coverImage.startsWith('/') ? post.coverImage : '/' + post.coverImage}`,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      author: {
        '@type': 'Person',
        name: post.author,
        url: `${SITE_URL}${path('sobre')}#autor`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Bolão Elite',
        logo: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/logo-512.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': postUrl,
      },
      articleSection: t(`category.${post.category}`, post.category),
      keywords: post.keywords.join(', '),
      inLanguage: post.locale,
    };
  }, [post, postUrl, path, t]);

  if (!post) {
    return <Navigate to={blogPath} replace />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title={`${post.title} — ${t('post.seo_suffix', 'Blog Bolão Elite')}`}
        description={post.excerpt}
        canonical={postUrl}
        ogUrl={postUrl}
        ogImage={`${SITE_URL}${post.coverImage.startsWith('/') ? post.coverImage : '/' + post.coverImage}`}
        keywords={post.keywords.join(', ')}
        locale={post.locale}
        alternates={alternates}
        jsonLd={articleSchema}
      />

      <PublicHeader />

      <PostHeader post={post} />

      <article
        className="
          max-w-3xl mx-auto px-4 sm:px-6 py-10
          [&_p]:text-base [&_p]:sm:text-lg [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:my-4
          [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-extrabold [&_h2]:text-gray-900 [&_h2]:tracking-tight [&_h2]:mt-12 [&_h2]:mb-4
          [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3]:mt-8 [&_h3]:mb-3
          [&_a]:text-primary-600 [&_a]:font-semibold hover:[&_a]:underline
          [&_strong]:text-gray-900 [&_strong]:font-bold
          [&_ul]:my-4 [&_ul]:pl-6 [&_ul]:list-disc [&_ul]:space-y-1
          [&_ol]:my-4 [&_ol]:pl-6 [&_ol]:list-decimal [&_ol]:space-y-1
          [&_li]:text-gray-700 [&_li]:leading-relaxed
          [&_blockquote]:border-l-4 [&_blockquote]:border-primary-500 [&_blockquote]:bg-primary-50 [&_blockquote]:py-3 [&_blockquote]:px-4 [&_blockquote]:rounded-r-lg [&_blockquote]:my-6 [&_blockquote]:italic [&_blockquote]:text-gray-700
          [&_img]:rounded-xl [&_img]:my-6 [&_img]:w-full [&_img]:h-auto
          [&_figure]:my-6
          [&_figcaption]:text-xs [&_figcaption]:text-gray-500 [&_figcaption]:text-center [&_figcaption]:mt-2
          [&_hr]:my-10 [&_hr]:border-gray-200
          [&_table]:my-6 [&_table]:w-full [&_table]:text-sm [&_table]:border-collapse
          [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-gray-700 [&_th]:border [&_th]:border-gray-200
          [&_td]:px-3 [&_td]:py-2 [&_td]:text-gray-700 [&_td]:border [&_td]:border-gray-200
        "
      >
        {post.content}
      </article>

      <div className="max-w-3xl mx-auto w-full px-4 sm:px-6">
        <AuthorBio name={post.author} />
      </div>

      {relatedPosts.length > 0 && (
        <section className="max-w-5xl mx-auto w-full px-4 sm:px-6 py-12">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-6">
            {t('post.continue_reading', 'Continue lendo')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedPosts.map(p => (
              <PostCard key={p.slug} post={p} compact />
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link
              to={blogPath}
              className="inline-block text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              {t('post.all_articles', '← Ver todos os artigos do blog')}
            </Link>
          </div>
        </section>
      )}

      <PublicFooter />
    </div>
  );
}
